/**
 * Notifications store (DEV-1017) — лента + счётчик непрочитанных для колокольчика
 * кабинета агента. Realtime — по SSE (стрим /api/v1/notifications/stream), а не
 * поллингом: новое уведомление прилетает событием и добавляется в ленту вживую.
 */
import { defineStore } from 'pinia'
import { isImpersonating, tokenStorage } from '@/features/auth/tokenStorage'
import { ref } from 'vue'
import { API_CONFIG } from '@/api'
import {
  NOTIFICATION_APP,
  type ChannelPreference,
  type Notification,
  type TypePreference,
} from '../types/notifications.types'
import { mapNotification, notificationsApi } from '../api/notifications.api'

// Пауза перед переоткрытием SSE: EventSource переподключается сам, но токен зашит в
// URL при коннекте — переоткрываем со свежим токеном (переживает refresh access-токена).
const RECONNECT_DELAY = 5000

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(20)
  const isLoading = ref(false)
  const isPanelOpen = ref(false)
  // Настройки каналов и типов (страница настроек). Нет записи в eventPreferences = включён.
  const preferences = ref<ChannelPreference[]>([])
  const eventPreferences = ref<TypePreference[]>([])

  let es: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let closedByUs = false

  async function fetchUnreadCount() {
    try {
      unreadCount.value = await notificationsApi.getUnreadCount()
    } catch {
      /* тихо: колокольчик не критичен */
    }
  }

  async function fetchNotifications(p = 1) {
    isLoading.value = true
    try {
      const result = await notificationsApi.listNotifications(p, pageSize.value)
      if (p === 1) {
        notifications.value = result.notifications
      } else {
        notifications.value.push(...result.notifications)
      }
      total.value = result.total
      page.value = result.page
    } catch {
      /* тихо */
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore() {
    // Без этой проверки быстрый скролл слал два запроса на одну и ту же
    // страницу и дублировал элементы в ленте.
    if (isLoading.value) return
    if (notifications.value.length >= total.value) return
    await fetchNotifications(page.value + 1)
  }

  async function markRead(notificationId: string) {
    try {
      await notificationsApi.markRead(notificationId)
      const notif = notifications.value.find((n) => n.id === notificationId)
      if (notif && !notif.isRead) {
        notif.isRead = true
        notif.readAt = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch {
      /* тихо */
    }
  }

  async function markAllRead() {
    try {
      const count = await notificationsApi.markAllRead()
      notifications.value.forEach((n) => {
        if (!n.isRead) {
          n.isRead = true
          n.readAt = new Date().toISOString()
        }
      })
      unreadCount.value = 0
      return count
    } catch {
      return 0
    }
  }

  async function fetchPreferences() {
    try {
      preferences.value = await notificationsApi.getPreferences()
    } catch {
      /* тихо */
    }
  }

  async function updatePreferences(prefs: ChannelPreference[]) {
    await notificationsApi.updatePreferences(prefs)
    preferences.value = prefs
  }

  async function fetchEventPreferences() {
    try {
      eventPreferences.value = await notificationsApi.getEventPreferences()
    } catch {
      /* тихо */
    }
  }

  /** Upsert настроек типов: локально мержим по notificationType. */
  async function updateEventPreferences(prefs: TypePreference[]) {
    await notificationsApi.updateEventPreferences(prefs)
    const merged = new Map(eventPreferences.value.map((p) => [p.notificationType, p]))
    prefs.forEach((p) => merged.set(p.notificationType, p))
    eventPreferences.value = [...merged.values()]
  }

  function togglePanel() {
    isPanelOpen.value = !isPanelOpen.value
    if (isPanelOpen.value && notifications.value.length === 0) {
      fetchNotifications()
    }
  }

  function closePanel() {
    isPanelOpen.value = false
  }

  // --- SSE realtime ---

  function onIncoming(n: Notification) {
    if (notifications.value.some((x) => x.id === n.id)) return
    notifications.value.unshift(n)
    total.value += 1
    if (!n.isRead) unreadCount.value += 1
  }

  function openStream() {
    // Вкладка просмотра живой поток не открывает (DEV-2400): уведомления агента
    // не должны прилетать в браузер администратора, а SSE проверяет токен один
    // раз при подключении и пережил бы конец сеанса. Список и счётчик читаются
    // обычными запросами.
    if (es || isImpersonating()) return
    const token = tokenStorage.getAccessToken()
    if (!token) return

    const base = API_CONFIG.BASE_URL || ''
    // app в query — иначе хаб шлёт в эту вкладку и уведомления CRM с трекером:
    // ключ подписки на бэкенде составной (DEV-1961).
    es = new EventSource(
      `${base}/api/v1/notifications/stream` +
        `?token=${encodeURIComponent(token)}&app=${encodeURIComponent(NOTIFICATION_APP)}`,
    )

    es.addEventListener('notification', (ev) => {
      try {
        onIncoming(mapNotification(JSON.parse((ev as MessageEvent).data)))
      } catch {
        /* игнорируем битый payload */
      }
    })

    es.onopen = () => {
      // реконсиляция: подтягиваем актуальный счётчик (могли пропустить события в оффлайне).
      fetchUnreadCount()
    }

    es.onerror = () => {
      if (es) {
        es.close()
        es = null
      }
      if (!closedByUs) scheduleReconnect()
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer) return
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      // Счётчик тянем перед переподключением: если поток оборвался из-за
      // протухшего токена, обычный REST-запрос его обновит через интерцептор,
      // а EventSource этого не умеет и долбился бы вечно.
      fetchUnreadCount()
      openStream()
    }, RECONNECT_DELAY)
  }

  function connectStream() {
    closedByUs = false
    fetchUnreadCount()
    openStream()
  }

  function disconnectStream() {
    closedByUs = true
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (es) {
      es.close()
      es = null
    }
  }

  return {
    notifications,
    unreadCount,
    total,
    page,
    pageSize,
    isLoading,
    isPanelOpen,
    preferences,
    eventPreferences,
    fetchUnreadCount,
    fetchNotifications,
    loadMore,
    markRead,
    markAllRead,
    fetchPreferences,
    updatePreferences,
    fetchEventPreferences,
    updateEventPreferences,
    togglePanel,
    closePanel,
    connectStream,
    disconnectStream,
  }
})
